import { FC, MouseEvent, ReactElement, useMemo, useState } from 'react';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { Button } from '@savgroup-front-common/core/src/atoms/button';
import { Tag } from '@savgroup-front-common/core/src/atoms/Tag';
import { TAG_VARIANT } from '@savgroup-front-common/core/src/atoms/Tag/Tag.types';
import { Accordion } from '@savgroup-front-common/core/src/molecules/Accordion';
import { SUPPORTED_COMPONENTS } from '@savgroup-front-common/types';

import type { SubmitRuleCategory, RuleModificationType } from './submitRuleMockData';
import {
  $AccordionHeaderActions,
  $AccordionTitle,
  $CurrentValue,
  $PreviousValue,
  $RulesTableCell,
  $RulesTableHeader,
  $RulesTableRow,
  $RulesTableWrapper,
} from './SubmitRulePage.styles';

const MODIFICATION_LABELS: Record<RuleModificationType, string> = {
  created: 'Créé',
  updated: 'Modifié',
  deleted: 'Supprimé',
};

const MODIFICATION_VARIANTS: Record<
  RuleModificationType,
  (typeof TAG_VARIANT)[keyof typeof TAG_VARIANT]
> = {
  created: TAG_VARIANT.SUCCESS,
  updated: TAG_VARIANT.PRIMARY,
  deleted: TAG_VARIANT.DANGER,
};

interface SubmitRuleSummarySectionProps {
  category: SubmitRuleCategory;
  isPublished: boolean;
  onPublishBlock: (importType: string) => void;
}

const SubmitRuleSummarySection: FC<SubmitRuleSummarySectionProps> = ({
  category,
  isPublished,
  onPublishBlock,
}) => {
  const [isOpen, setIsOpen] = useState(category.rules.length > 0 && !isPublished);
  const ruleCount = category.rules.length;
  const hasPendingRules = ruleCount > 0 && !isPublished;
  const isDisabled = ruleCount === 0 && !isPublished;

  const columnLabels = useMemo(() => {
    const firstRule = category.rules[0];

    return firstRule?.cells.map((cell) => cell.label) ?? [];
  }, [category.rules]);

  const handlePublishClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onPublishBlock(category.importType);
  };

  const headerActions = hasPendingRules ? (
    <$AccordionHeaderActions onClick={(event) => event.stopPropagation()}>
      <Button
        primary
        small
        type={BUTTON_TYPES.BUTTON}
        onClick={handlePublishClick}
        data-testid={`publish-block-${category.importType}`}
      >
        Publier
      </Button>
    </$AccordionHeaderActions>
  ) : undefined;

  const accordionTitle: ReactElement = (
    <$AccordionTitle>
      <span>{category.title}</span>
      {isPublished ? (
        <Tag variant={TAG_VARIANT.SUCCESS} withoutMargin>
          Publié
        </Tag>
      ) : (
        <Tag variant={TAG_VARIANT.PRIMARY} disabled={ruleCount === 0} withoutMargin>
          {ruleCount}
        </Tag>
      )}
    </$AccordionTitle>
  );

  return (
    <Accordion
      customTitle={accordionTitle}
      headerRightChildren={headerActions}
      isOpen={isDisabled ? false : isOpen}
      isDisabled={isDisabled}
      isChevronOnRightSide
      componentThemeName={SUPPORTED_COMPONENTS.CONFIGURATION_CONSOLE_SUMMARY_RULE}
      onClick={() => {
        if (!isDisabled) {
          setIsOpen((prev) => !prev);
        }
      }}
      dataTestId={`submit-rule-category-${category.importType}`}
    >
      {isPublished && (
        <p style={{ margin: '0.75rem 0 0', fontSize: '0.875rem', color: '#666' }}>
          Ce bloc a été publié. Les règles ne sont plus en brouillon.
        </p>
      )}
      {hasPendingRules && (
        <$RulesTableWrapper>
          <$RulesTableHeader $columnCount={columnLabels.length}>
            <$RulesTableCell>Modification</$RulesTableCell>
            {columnLabels.map((label) => (
              <$RulesTableCell key={label}>{label}</$RulesTableCell>
            ))}
          </$RulesTableHeader>
          {category.rules.map((rule) => (
            <$RulesTableRow key={rule.id} $columnCount={columnLabels.length}>
              <$RulesTableCell>
                <Tag variant={MODIFICATION_VARIANTS[rule.modificationType]} withoutMargin>
                  {MODIFICATION_LABELS[rule.modificationType]}
                </Tag>
              </$RulesTableCell>
              {rule.cells.map((cell) => (
                <$RulesTableCell key={`${rule.id}-${cell.label}`}>
                  {cell.previousValue ? (
                    <>
                      <$PreviousValue>{cell.previousValue}</$PreviousValue>
                      <$CurrentValue>{cell.value}</$CurrentValue>
                    </>
                  ) : (
                    <$CurrentValue>{cell.value}</$CurrentValue>
                  )}
                </$RulesTableCell>
              ))}
            </$RulesTableRow>
          ))}
        </$RulesTableWrapper>
      )}
    </Accordion>
  );
};

export default SubmitRuleSummarySection;
